import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "@/features/auth/AuthContext";
import { lerPreferencia, salvarPreferencia } from "@/features/preferences/api";
import { getSessionGeneration } from "@/lib/http";

interface Preferences {
  version: 1;
  favorites: string[];
  expandedGroups: string[];
  collapsedGroups: string[];
}
export interface NavigationPreferences {
  favorites: string[];
  expandedGroups: string[];
  collapsedGroups: string[];
  status: "loading" | "ready" | "saving" | "error";
  error: string | null;
  reload: () => void;
  toggleFavorite: (id: string) => Promise<void>;
  setGroupExpanded: (id: string, expanded: boolean) => Promise<void>;
  /** Alterna a preferência explícita; abertura automática da rota pertence à UI. */
  toggleGroup: (id: string) => Promise<void>;
}

const EMPTY: Preferences = { version: 1, favorites: [], expandedGroups: [], collapsedGroups: [] };
const LOCAL_KEY = "ghm:navegacao:bancada:v1";
const validId = (id: unknown): id is string => typeof id === "string" && id.length > 0
  && id.length <= 200 && id.trim() === id;

function parse(value: unknown): Preferences {
  if (value === null) return EMPTY;
  const v = value as Partial<Preferences> | undefined;
  const ids = (input: unknown): input is string[] => Array.isArray(input) && input.length <= 512
    && input.every(validId) && new Set(input).size === input.length;
  if (!v || v.version !== 1 || !ids(v.favorites) || !ids(v.expandedGroups) || !ids(v.collapsedGroups)
    || v.expandedGroups.some(id => v.collapsedGroups!.includes(id)) || JSON.stringify(v).length > 16000) {
    throw new Error("Preferência de navegação inválida");
  }
  // IDs ausentes da lista atual continuam salvos: esconder/excluir um grupo
  // ou receber uma lista parcial não pode apagar as outras escolhas da pessoa.
  return { version: 1, favorites: v.favorites, expandedGroups: v.expandedGroups, collapsedGroups: v.collapsedGroups };
}

// Somente PUTs: um novo GET aguarda gravações anteriores da mesma pessoa/org,
// mas não fica preso a um GET abortado. Não guarda valores entre montagens.
const pendingWrites = new Map<string, Promise<void>>();
function enqueue(key: string, task: () => Promise<void>): Promise<void> {
  const job = (pendingWrites.get(key) ?? Promise.resolve()).then(task, task);
  pendingWrites.set(key, job);
  const cleanup = () => { if (pendingWrites.get(key) === job) pendingWrites.delete(key); };
  void job.then(cleanup, cleanup);
  return job;
}

export function useNavigationPreferences(): NavigationPreferences {
  const { usuario, aberto, isCurrentUser } = useAuth();
  const userId = usuario?.id, organizationId = usuario?.organizacao?.organizationId;
  const generation = getSessionGeneration();
  const scope = useMemo(() => ({
    mode: aberto ? "local" as const : userId && organizationId ? "server" as const : "inactive" as const,
    key: `navegacao:${organizationId}`, userId, generation,
    queueKey: JSON.stringify([aberto ? "bancada" : "pessoal", userId, organizationId]),
    active: false, readEpoch: 0, readOk: false, revision: 0, writes: 0,
    confirmed: EMPTY, value: EMPTY,
  }), [aberto, userId, organizationId, generation]);
  const [read, setRead] = useState(0);
  const [view, setView] = useState({ scope, value: EMPTY, status: "loading" as NavigationPreferences["status"], error: null as string | null });

  useEffect(() => {
    scope.active = true;
    scope.readOk = false;
    const epoch = ++scope.readEpoch;
    const controller = new AbortController();
    const current = () => scope.active && scope.readEpoch === epoch && !controller.signal.aborted
      && scope.generation === getSessionGeneration()
      && (scope.mode === "local" || isCurrentUser(scope.userId));
    if (scope.mode === "inactive") {
      setView({ scope, value: EMPTY, status: "loading", error: null });
    } else {
      setView({ scope, value: scope.confirmed, status: "loading", error: null });
      void (async () => {
        try {
          await pendingWrites.get(scope.queueKey);
          if (!current()) return;
          const stored = scope.mode === "local"
            ? JSON.parse(window.localStorage.getItem(LOCAL_KEY) ?? "null") as unknown
            : await lerPreferencia<unknown>(scope.key, controller.signal);
          if (!current()) return;
          scope.confirmed = parse(stored);
          scope.value = scope.confirmed;
          scope.readOk = true;
          setView({ scope, value: scope.confirmed, status: "ready", error: null });
        } catch {
          if (!current()) return;
          setView({ scope, value: scope.confirmed, status: "error",
            error: "Não foi possível carregar suas preferências de navegação. Recarregue antes de salvar." });
        }
      })();
    }
    return () => { scope.active = false; controller.abort(); };
  }, [scope, read, isCurrentUser]);

  const update = useCallback((change: (value: Preferences) => Preferences): Promise<void> => {
    const current = () => scope.generation === getSessionGeneration()
      && (scope.mode === "local" || isCurrentUser(scope.userId));
    if (!scope.active || !scope.readOk || !current()) return Promise.resolve();
    const next = change(scope.value);
    if (next === scope.value) return Promise.resolve();
    let parsed: Preferences;
    try { parsed = parse(next); }
    catch {
      scope.readOk = false;
      scope.value = scope.confirmed;
      setView({ scope, value: scope.confirmed, status: "error", error: "A seleção excede o limite das preferências de navegação. Recarregue antes de salvar." });
      return Promise.resolve();
    }
    const revision = ++scope.revision;
    scope.value = parsed;
    scope.writes += 1;
    setView({ scope, value: parsed, status: "saving", error: null });
    return enqueue(scope.queueKey, async () => {
      try {
        // Escolhas já aceitas concluem ao sair da rota, mas nunca após logout
        // ou novo login, mesmo quando o novo usuário tem o mesmo ID.
        if (!scope.readOk || !current()) return;
        if (scope.mode === "local") window.localStorage.setItem(LOCAL_KEY, JSON.stringify(parsed));
        else await salvarPreferencia(scope.key, parsed);
        if (!current() || !scope.readOk) return;
        scope.confirmed = parsed;
        if (scope.active && scope.revision === revision) {
          setView({ scope, value: parsed, status: "ready", error: null });
        }
      } catch {
        if (!current()) return;
        scope.readOk = false;
        scope.value = scope.confirmed;
        if (scope.active) setView({ scope, value: scope.confirmed, status: "error",
          error: "Não foi possível confirmar a gravação. A última seleção confirmada foi restaurada; recarregue antes de tentar novamente." });
      } finally { scope.writes -= 1; }
    });
  }, [scope, isCurrentUser]);

  const toggleFavorite = useCallback((id: string) => validId(id) ? update(value => ({ ...value,
    favorites: value.favorites.includes(id) ? value.favorites.filter(item => item !== id) : [...value.favorites, id],
  })) : Promise.resolve(), [update]);
  const setGroupExpanded = useCallback((id: string, expanded: boolean) => validId(id) ? update(value => {
    if ((expanded ? value.expandedGroups : value.collapsedGroups).includes(id)) return value;
    return { ...value,
      expandedGroups: [...value.expandedGroups.filter(item => item !== id), ...(expanded ? [id] : [])],
      collapsedGroups: [...value.collapsedGroups.filter(item => item !== id), ...(expanded ? [] : [id])],
    };
  }) : Promise.resolve(), [update]);
  const toggleGroup = useCallback((id: string) => setGroupExpanded(id, !scope.value.expandedGroups.includes(id)), [scope, setGroupExpanded]);
  const reload = useCallback(() => { if (scope.writes === 0) setRead(value => value + 1); }, [scope]);
  const visible = view.scope === scope ? view : { value: EMPTY, status: "loading" as const, error: null };
  return { favorites: visible.value.favorites, expandedGroups: visible.value.expandedGroups,
    collapsedGroups: visible.value.collapsedGroups, status: visible.status, error: visible.error,
    reload, toggleFavorite, setGroupExpanded, toggleGroup };
}
